import type { Antworten, Funnel, Schritt } from "@fbs/funnel-schema";

/**
 * Vom Antwortsatz zur Anfrage an die API.
 *
 * Der Funnel kennt nur Schritte mit frei gewählten Kennungen. Die API erwartet
 * Kontaktdaten, Postleitzahl und Einwilligung an festen Stellen. Diese Datei
 * übersetzt das eine ins andere, und zwar über den Typ des Schritts, nicht
 * über seinen Namen: Ein Kontaktschritt bleibt ein Kontaktschritt, auch wenn
 * er im Builder "kontakt_neu_2" heißt.
 */

export interface Absendedaten {
  kontakt: { first_name: string; last_name: string; email: string; phone: string };
  postcode?: string | undefined;
  city?: string | undefined;
  street?: string | undefined;
  consent: { privacy: boolean; marketing?: boolean };
}

function textVon(quelle: Record<string, unknown>, feld: string): string {
  const wert = quelle[feld];
  return typeof wert === "string" ? wert.trim() : "";
}

function objektVon(wert: unknown): Record<string, unknown> {
  return typeof wert === "object" && wert !== null && !Array.isArray(wert)
    ? (wert as Record<string, unknown>)
    : {};
}

function ersterVomTyp<T extends Schritt["type"]>(
  funnel: Funnel,
  typ: T,
): Extract<Schritt, { type: T }> | undefined {
  return funnel.steps.find((s): s is Extract<Schritt, { type: T }> => s.type === typ);
}

export function absendedatenBauen(funnel: Funnel, antworten: Antworten): Absendedaten {
  const kontaktSchritt = ersterVomTyp(funnel, "contact");
  const kontaktwerte = objektVon(
    kontaktSchritt ? antworten[kontaktSchritt.id] : undefined,
  );

  const plzSchritt = ersterVomTyp(funnel, "postcode");
  const plzWert = plzSchritt ? antworten[plzSchritt.id] : undefined;
  /*
   * Der Postleitzahlschritt liefert eine Zeichenkette, solange er nur die
   * Postleitzahl abfragt, und ein Objekt sobald Ort oder Straße dazukommen.
   * Beide Formen kommen vor - in zwölf Funnels die eine, in Trockenbau die
   * andere, und in gespeicherten Sitzungen beide nebeneinander.
   */
  const plzTeile = objektVon(
    typeof plzWert === "string" ? { postcode: plzWert } : plzWert,
  );
  const plz = textVon(plzTeile, "postcode").replace(/\s/g, "");
  const ort = textVon(plzTeile, "city");
  const strasseAusAdresse = textVon(plzTeile, "street");

  const einwilligungSchritt = ersterVomTyp(funnel, "consent");
  const einwilligungen = objektVon(
    einwilligungSchritt ? antworten[einwilligungSchritt.id] : undefined,
  );

  /*
   * Haken koennen auch unter den Kontaktfeldern stehen.
   *
   * Dann liegen sie im selben Antwortobjekt wie Name und E-Mail. Fuer die
   * Einwilligung zaehlen sie genauso wie ein eigener Schritt - der Haken ist
   * derselbe, nur an einer anderen Stelle gesetzt.
   */
  const hakenImKontakt = kontaktSchritt?.consent_items ?? [];
  const kontakthaken = hakenImKontakt.length > 0 ? kontaktwerte : {};
  const pflichthaken = hakenImKontakt.find((p) => p.required);

  /**
   * Welcher Haken ist die Datenschutzeinwilligung?
   *
   * Der Schlüssel "privacy" ist die Übereinkunft. Heißt er anders, gilt der
   * erste Pflichthaken, denn ein Pflichthaken kann nichts anderes sein als
   * die Einwilligung, ohne die der Lead nicht weitergegeben werden darf.
   */
  const datenschutzSchluessel =
    einwilligungSchritt?.items.find((i) => i.key === "privacy")?.key ??
    einwilligungSchritt?.items.find((i) => i.required)?.key ??
    "privacy";

  const werbung = einwilligungen["marketing"];

  return {
    kontakt: {
      first_name: textVon(kontaktwerte, "first_name"),
      last_name: textVon(kontaktwerte, "last_name"),
      email: textVon(kontaktwerte, "email"),
      phone: textVon(kontaktwerte, "phone"),
    },
    postcode: plz === "" ? undefined : plz,
    city: ort || undefined,
    // Die Adressfrage hat Vorrang: Dort steht die Anschrift des Objekts, im
    // Kontaktschritt die des Auftraggebers. Fuer die Fachfirma zaehlt das Objekt.
    street: strasseAusAdresse || textVon(kontaktwerte, "street") || undefined,
    consent: {
      /*
       * Ohne Einwilligungsschritt gilt das Absenden als Einwilligung.
       *
       * Die API weist jeden Lead ohne Einwilligung ab - ohne diese Zeile
       * scheiterte in einem Funnel ohne Einwilligungsschritt jede einzelne
       * Anfrage, und zwar erst nach dem letzten Klick. Wer den Schritt
       * weglaesst, entscheidet sich damit fuer die stillschweigende Form:
       * Der Klick auf "Anfrage absenden" traegt die Zustimmung.
       *
       * Hat der Funnel einen Einwilligungsschritt, zaehlt weiter nur der
       * gesetzte Haken. Ein leerer Haken bleibt ein Nein.
       */
      privacy:
        pflichthaken !== undefined
          ? kontakthaken[pflichthaken.key] === true
          : einwilligungSchritt === undefined
            ? true
            : einwilligungen[datenschutzSchluessel] === true,
      ...(typeof werbung === "boolean" ? { marketing: werbung } : {}),
    },
  };
}
